// ============================================================
//  Cosmo — animated galaxy-alien mascot
//  Drawn entirely procedurally via Phaser Graphics
// ============================================================
import { C } from '../../constants/Colors.js';

type CosmoState = 'idle' | 'excited' | 'thinking' | 'celebrating' | 'encouraging' | 'surprised';

export class Cosmo extends Phaser.GameObjects.Container {
  private bodyGfx!: Phaser.GameObjects.Graphics;
  private eyesGfx!: Phaser.GameObjects.Graphics;
  private antennaGfx!: Phaser.GameObjects.Graphics;
  private glowGfx!: Phaser.GameObjects.Graphics;
  private cape!: Phaser.GameObjects.Graphics;
  private starGfx!: Phaser.GameObjects.Graphics;
  private galaxyDots!: Phaser.GameObjects.Graphics;
  private speechBubble!: Phaser.GameObjects.Container;
  private speechText!: Phaser.GameObjects.Text;
  private particles!: Phaser.GameObjects.Particles.ParticleEmitter | null;

  private floatTween!: Phaser.Tweens.Tween | null;
  private antennaTween!: Phaser.Tweens.Tween | null;
  private cosmoState: CosmoState = 'idle';
  private scale2 = 1;

  // Cached dot positions for galaxy texture
  private dotPositions: Array<{ x: number; y: number; r: number; a: number }> = [];

  constructor(scene: Phaser.Scene, x: number, y: number, scale2 = 1) {
    super(scene, x, y);
    this.scale2 = scale2;
    (scene as Phaser.Scene & { add: Phaser.GameObjects.GameObjectFactory }).add.existing(this as unknown as Phaser.GameObjects.Container);
    this.buildCharacter();
    this.buildSpeechBubble();
    this.startIdle();
  }

  private buildCharacter() {
    // Order: glow → cape → body → galaxy → star → eyes → antennae
    this.glowGfx    = this.scene.add.graphics();
    this.cape       = this.scene.add.graphics();
    this.bodyGfx       = this.scene.add.graphics();
    this.galaxyDots = this.scene.add.graphics();
    this.starGfx    = this.scene.add.graphics();
    this.eyesGfx    = this.scene.add.graphics();
    this.antennaGfx = this.scene.add.graphics();

    this.add([this.glowGfx, this.cape, this.bodyGfx, this.galaxyDots, this.starGfx, this.eyesGfx, this.antennaGfx]);

    this.setScale(this.scale2);
    this.drawAll();
  }

  private drawAll() {
    this.drawGlow();
    this.drawCape();
    this.drawBody();
    this.drawGalaxy();
    this.drawChestStar();
    this.drawEyes();
    this.drawAntennae();
  }

  private drawGlow() {
    const g = this.glowGfx;
    g.clear();
    // Outer aura
    for (let i = 6; i >= 1; i--) {
      g.fillStyle(C.cosmoPurple, 0.04 * i);
      g.fillEllipse(0, 10, 140 + i * 18, 180 + i * 18);
    }
  }

  private drawCape() {
    const g = this.cape;
    g.clear();
    // Flowing energy trail behind body — teardrop shape
    g.fillStyle(C.cosmoBlue, 0.35);
    g.fillEllipse(-8, 60, 70, 110);
    g.fillStyle(C.cosmoPurple, 0.25);
    g.fillEllipse(5, 65, 55, 95);
    g.fillStyle(C.nebulaCyan, 0.12);
    g.fillEllipse(-5, 75, 40, 70);
  }

  private drawBody() {
    const g = this.bodyGfx;
    g.clear();

    // Body gradient simulation: layers from purple to blue
    const bodyColors = [0x6a1fe0, 0x7b2fff, 0x8b3fff, 0x6a3fd0, 0x4060d0, 0x2090d0];
    const yOffsets = [0, 10, 20, 30, 40, 55];
    bodyColors.forEach((col, i) => {
      g.fillStyle(col, 0.85);
      g.fillEllipse(0, yOffsets[i] / 2, 82 - i * 2, 100 - i * 3);
    });

    // Final clean body shape
    g.fillStyle(0x7b2fff, 0.9);
    g.fillEllipse(0, 10, 80, 96);
    g.fillStyle(0x4a80e0, 0.5);
    g.fillEllipse(0, 25, 60, 60);

    // Head (circle, sits on body)
    g.fillStyle(0x7b2fff, 1);
    g.fillCircle(0, -52, 50);
    g.fillStyle(0x5030c0, 0.6);
    g.fillCircle(-8, -58, 32);
    g.fillStyle(0x9060ff, 0.4);
    g.fillCircle(5, -45, 20);

    // Neck connector
    g.fillStyle(0x6a2fd0, 1);
    g.fillRect(-15, -12, 30, 20);
  }

  private drawGalaxy() {
    const g = this.galaxyDots;
    g.clear();
    // Generate stable dot positions once
    if (this.dotPositions.length === 0) {
      for (let i = 0; i < 55; i++) {
        // Keep dots within body ellipse region
        const angle = Math.random() * Math.PI * 2;
        const rx = 35, ry = 48;
        const r2 = Math.random();
        const x = Math.cos(angle) * rx * r2;
        const y = Math.sin(angle) * ry * r2 + 5;
        const dotR = 0.5 + Math.random() * 1.5;
        const dotA = 0.3 + Math.random() * 0.6;
        this.dotPositions.push({ x, y, r: dotR, a: dotA });
      }
      // Head dots
      for (let i = 0; i < 30; i++) {
        const angle = Math.random() * Math.PI * 2;
        const r2 = Math.random() * 0.85;
        const x = Math.cos(angle) * 44 * r2;
        const y = Math.sin(angle) * 44 * r2 - 52;
        const dotR = 0.5 + Math.random() * 1.5;
        const dotA = 0.3 + Math.random() * 0.7;
        this.dotPositions.push({ x, y, r: dotR, a: dotA });
      }
    }

    // Draw cyan/white dots for galaxy texture
    this.dotPositions.forEach(({ x, y, r, a }) => {
      const col = Math.random() < 0.4 ? 0x00e5ff : 0xffffff;
      g.fillStyle(col, a);
      g.fillCircle(x, y, r);
    });
  }

  private drawChestStar() {
    const g = this.starGfx;
    g.clear();
    // Glow
    for (let i = 4; i >= 1; i--) {
      g.fillStyle(C.cosmoGoldStar, 0.08 * i);
      g.fillCircle(0, 15, 18 + i * 5);
    }
    // 5-pointed star
    g.fillStyle(C.cosmoGoldStar, 1);
    this.drawStar(g, 0, 15, 5, 14, 6);
    g.fillStyle(0xfffde7, 0.7);
    this.drawStar(g, -1, 14, 5, 7, 3);
  }

  private drawStar(g: Phaser.GameObjects.Graphics, cx: number, cy: number, points: number, outerR: number, innerR: number) {
    const path: { x: number; y: number }[] = [];
    for (let i = 0; i < points * 2; i++) {
      const angle = (i * Math.PI) / points - Math.PI / 2;
      const r = i % 2 === 0 ? outerR : innerR;
      path.push({ x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r });
    }
    g.fillPoints(path, true);
  }

  private drawEyes() {
    const g = this.eyesGfx;
    g.clear();

    const eyes = [{ x: -20, y: -52 }, { x: 20, y: -52 }];
    eyes.forEach(({ x, y }) => {
      // White sclera
      g.fillStyle(0xffffff, 1);
      g.fillCircle(x, y, 18);
      // Iris (cyan-purple)
      g.fillStyle(0x3090ff, 1);
      g.fillCircle(x, y, 13);
      // Pupil
      g.fillStyle(0x050015, 1);
      g.fillCircle(x, y, 8);
      // Highlight
      g.fillStyle(0xffffff, 0.95);
      g.fillCircle(x + 5, y - 5, 4);
      g.fillStyle(0xffffff, 0.5);
      g.fillCircle(x - 4, y + 3, 2);
    });
  }

  private drawAntennae() {
    const g = this.antennaGfx;
    g.clear();

    // Left antenna
    g.lineStyle(3, 0xc0a0ff, 1);
    g.beginPath();
    g.moveTo(-12, -98);
    g.lineTo(-28, -140);
    g.strokePath();

    // Right antenna
    g.beginPath();
    g.moveTo(12, -98);
    g.lineTo(28, -140);
    g.strokePath();

    // Glowing tips
    const tips = [{ x: -28, y: -140 }, { x: 28, y: -140 }];
    tips.forEach(({ x, y }) => {
      for (let i = 4; i >= 1; i--) {
        g.fillStyle(C.cosmoGoldStar, 0.12 * i);
        g.fillCircle(x, y, 10 + i * 4);
      }
      g.fillStyle(C.cosmoGoldStar, 1);
      g.fillCircle(x, y, 7);
      g.fillStyle(0xfffde7, 0.9);
      g.fillCircle(x - 2, y - 2, 3);
    });
  }

  private buildSpeechBubble() {
    this.speechBubble = this.scene.add.container(70, -80);
    const bg = this.scene.add.graphics();
    const w = 220, h = 80;
    bg.fillStyle(0xffffff, 0.95);
    bg.fillRoundedRect(0, 0, w, h, 14);
    bg.fillStyle(0xe8d8ff, 0.6);
    bg.fillRoundedRect(4, 4, w - 8, h - 8, 11);
    // Tail
    bg.fillStyle(0xffffff, 0.95);
    bg.fillTriangle(-14, h * 0.4, 0, h * 0.3, 0, h * 0.6);

    this.speechText = this.scene.add.text(12, 10, '', {
      fontFamily: '"Nunito", "Segoe UI", Arial, sans-serif',
      fontSize: '15px',
      color: '#1a0a3e',
      wordWrap: { width: 196 },
      lineSpacing: 4,
    });

    this.speechBubble.add([bg, this.speechText]);
    this.speechBubble.setVisible(false);
    this.add(this.speechBubble);
  }

  // ── Public interface ─────────────────────────────────────────

  say(text: string, duration = 3500) {
    this.speechText.setText(text);
    this.speechBubble.setVisible(true);
    this.speechBubble.setAlpha(0);
    this.scene.tweens.add({
      targets: this.speechBubble,
      alpha: 1,
      duration: 250,
      onComplete: () => {
        if (duration > 0) {
          this.scene.time.delayedCall(duration, () => this.hideSpeech());
        }
      },
    });
  }

  hideSpeech() {
    this.scene.tweens.add({
      targets: this.speechBubble,
      alpha: 0,
      duration: 250,
      onComplete: () => this.speechBubble.setVisible(false),
    });
  }

  celebrate() {
    if (this.cosmoState === 'celebrating') return;
    this.cosmoState = 'celebrating';
    this.stopFloat();

    // Jump up
    this.scene.tweens.add({
      targets: this,
      y: this.y - 50,
      duration: 280,
      ease: 'Back.easeOut',
      yoyo: true,
      repeat: 2,
      onComplete: () => {
        this.cosmoState = 'idle';
        this.startIdle();
      },
    });

    // Spin
    this.scene.tweens.add({
      targets: this,
      angle: 360,
      duration: 700,
      ease: 'Sine.easeInOut',
    });

    // Burst particles from chest star
    this.emitCelebrationParticles();
  }

  think() {
    if (this.cosmoState === 'thinking') return;
    this.cosmoState = 'thinking';
    this.stopFloat();
    this.scene.tweens.add({
      targets: this,
      angle: -12,
      duration: 400,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: 2,
      onComplete: () => {
        this.scene.tweens.add({ targets: this, angle: 0, duration: 200 });
        this.cosmoState = 'idle';
        this.startIdle();
      },
    });
  }

  encourage() {
    this.say("You can do it! Keep going! ✨");
    this.scene.tweens.add({
      targets: this,
      scaleX: this.scale2 * 1.08,
      scaleY: this.scale2 * 1.08,
      duration: 220,
      yoyo: true,
    });
  }

  surprised() {
    this.stopFloat();
    this.cosmoState = 'surprised';
    this.scene.tweens.add({
      targets: this,
      y: this.y - 20,
      duration: 150,
      ease: 'Back.easeOut',
      yoyo: true,
      onComplete: () => {
        this.cosmoState = 'idle';
        this.startIdle();
      },
    });
  }

  private emitCelebrationParticles() {
    try {
      const emitter = this.scene.add.particles(this.x, this.y - 30, 'particle_star', {
        speed: { min: 80, max: 220 },
        angle: { min: 0, max: 360 },
        scale: { start: 1.2, end: 0 },
        alpha: { start: 1, end: 0 },
        lifespan: 900,
        quantity: 18,
        tint: [0xffd700, 0xff8c00, 0x00e5ff, 0xff4dbd],
      });
      this.scene.time.delayedCall(300, () => emitter.stop());
      this.scene.time.delayedCall(1400, () => emitter.destroy());
    } catch { /* particles unavailable */ }
  }

  private startIdle() {
    if (this.floatTween) return;
    this.floatTween = this.scene.tweens.add({
      targets: this,
      y: this.y - 12,
      duration: 2400,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    // Pulse antenna tips every 3s
    this.antennaTween = this.scene.tweens.add({
      targets: this.antennaGfx,
      alpha: 0.4,
      duration: 800,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      delay: 1200,
    });
  }

  private stopFloat() {
    if (this.floatTween) {
      this.floatTween.stop();
      this.floatTween = null;
    }
  }

  getState() { return this.cosmoState; }

  destroy(fromScene?: boolean) {
    this.stopFloat();
    if (this.antennaTween) this.antennaTween.stop();
    super.destroy(fromScene);
  }
}
